import { useEffect, useRef } from 'react';
import CoreboundIcon from './CoreboundIcon';
import { BoardEffectTile, isBoardEffectTile } from './BoardEffectIcons';
import {
  getBoardTileAbility,
  getBoardSquareLabel,
  getBoardSquareColor,
} from './doorBoard';
import { getPendingBoardTile } from './boardTileEffects';
import { RELICS } from './relics';
import SabotageCutscene from './SabotageCutscene';
import { formatDieValue } from './dice';
import { playSound } from '../../utils/utils';
import './BoardTileChoice.css';
import { usePopupTransition } from './popupTransition';

const BoardTileChoice = ({ game, onChoose, reduceMotion = false }) => {
  const pending = getPendingBoardTile(game);
  const { close, popupClass } = usePopupTransition(undefined, {
    transitionKey: pending?.id,
  });
  const headingRef = useRef(null);
  const dialogRef = useRef(null);
  const isGambitPrompt = pending?.type === 'gambit' && !pending.relaunch;
  useEffect(() => {
    if (pending && pending.type !== 'powerUp')
      headingRef.current?.focus({ preventScroll: true });
  }, [pending]);
  const logs = game.boardTileResolution?.logs || [];
  if (pending?.type === 'gambit' && pending.relaunch) return null;
  if (!pending)
    return logs.length ? (
      <div className="coreboundTileReport" role="status">
        {logs.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
    ) : null;
  const ability = getBoardTileAbility(pending);
  const choose = (value, { sound = true } = {}) => {
    if (sound) {
      if (
        pending.type === 'relay' ||
        (pending.type === 'gambit' && value === 'reroll')
      )
        playSound('/sounds/glitch-sound.mp3', { preventOverlap: true });
      else playSound('/sounds/menu-select.mp3');
      if (pending.type === 'recharge' && pending.options.includes(value))
        playSound('/sounds/recharge-ability.mp3');
    }
    close(() => onChoose?.({ id: pending.id, value }));
  };
  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      choose('keep');
      return;
    }
    if (event.key !== 'Tab') return;
    const buttons = dialogRef.current?.querySelectorAll(
      'button:not(:disabled)'
    );
    if (!buttons?.length) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  if (pending.type === 'sabotage' && pending.cutscene)
    return (
      <SabotageCutscene
        board={game.bossDoorBoard}
        pending={pending}
        reduceMotion={reduceMotion}
        onComplete={() => choose('complete', { sound: false })}
        onSkip={() => choose('complete')}
      />
    );
  const board = game.doorBoard;
  const grid = pending.type === 'relay';
  const choicePanel = (
    <section
      ref={dialogRef}
      className={`coreboundTileChoice ${popupClass}`}
      style={{ '--tile-accent': ability.color }}
      role={isGambitPrompt ? 'dialog' : undefined}
      aria-modal={isGambitPrompt ? 'true' : undefined}
      aria-labelledby="corebound-tile-choice-title"
      onKeyDown={isGambitPrompt ? handleDialogKeyDown : undefined}
    >
      <header>
        <span className="coreboundTileChoiceIcon" aria-hidden="true">
          <CoreboundIcon name={pending.type} />
        </span>
        <div>
          <span className="coreboundPanelKicker">
            {pending.type === 'powerUp'
              ? 'ENERGY SURGE · AUTOMATIC'
              : isGambitPrompt
              ? 'GAMBIT TILE · YOUR CALL'
              : 'TILE ACTIVATED · YOUR CHOICE'}
          </span>
          <h3 ref={headingRef} tabIndex={-1} id="corebound-tile-choice-title">
            {ability.name}
          </h3>
        </div>
      </header>
      <p>{ability.description}</p>
      {pending.type === 'powerUp' && (
        <div className="coreboundTileGambitValue" role="status">
          <span>{ability.short} · RELAUNCHING</span>
          <strong>
            {pending.before} → {pending.after}
          </strong>
        </div>
      )}
      {grid && (
        <>
          <p className="coreboundTileChoiceHint">
            Select a highlighted neighbor. The die stays where it landed; only
            the effect changes.
          </p>
          <div
            className="coreboundTileChoiceGrid"
            role="group"
            aria-label="Choose a neighboring tile"
          >
            {(board || []).map((square, index) => (
              <button
                key={index}
                type="button"
                disabled={!pending.options.includes(index)}
                className={
                  index === pending.index && pending.type === 'relay'
                    ? 'is-origin'
                    : ''
                }
                style={{ '--cell-accent': getBoardSquareColor(square) }}
                title={getBoardSquareLabel(square)}
                aria-label={`Square ${index + 1}: ${getBoardSquareLabel(
                  square
                )}`}
                onClick={() => choose(index)}
              >
                <small>{index + 1}</small>
                {isBoardEffectTile(square) ? (
                  <BoardEffectTile effect={square.effect} />
                ) : (
                  <span>{getBoardSquareLabel(square)}</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
      {pending.type === 'gambit' && (
        <>
          <div className="coreboundTileGambitValue">
            <span>CURRENT ROLL</span>
            <strong>{formatDieValue(game.roll)}</strong>
          </div>
          <p className="coreboundTileGambitPrompt">
            Spend 1 shard to reroll? The die will launch again and its new
            landing replaces this one.
          </p>
          <div className="coreboundTileChoiceActions">
            <button type="button" onClick={() => choose('keep')}>
              No, keep {formatDieValue(game.roll)}
            </button>
            <button
              type="button"
              disabled={game.shards < 1}
              onClick={() => choose('reroll')}
            >
              Yes · spend 1 shard
            </button>
          </div>
          <small className="coreboundTileGambitShardNote">
            {game.shards} {game.shards === 1 ? 'shard' : 'shards'} available ·
            One reroll per throw. Your selected die’s faces still apply.
          </small>
        </>
      )}
      {pending.type === 'recharge' && (
        <div className="coreboundTileChoiceActions">
          {pending.options.map((id) => (
            <button key={id} type="button" onClick={() => choose(id)}>
              Restore {RELICS[id].name}
            </button>
          ))}
        </div>
      )}
    </section>
  );

  return isGambitPrompt ? (
    <div className="coreboundGambitBackdrop">{choicePanel}</div>
  ) : (
    choicePanel
  );
};

export default BoardTileChoice;
